import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';

interface AccountSettingsProps {
  fullName: string;
  email: string;
}

export default function AccountSettings({ fullName, email }: AccountSettingsProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(fullName);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    try {
      const { error: err } = await supabase.auth.updateUser({
        data: { full_name: name },
      });
      if (err) {
        setError(err.message);
      } else {
        setMessage(t('profile.updated'));
      }
    } catch {
      setError(t('auth.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-secondary-200 rounded-lg p-6">
        <h3 className="font-heading text-lg font-bold text-secondary-900">{t('profile.personalInfo')}</h3>
        <p className="mt-1 text-sm text-secondary-500">
          {t('profile.updateName')}
        </p>

        <form onSubmit={handleSave} className="mt-6 space-y-5" data-readdy-form>
          <div>
            <label htmlFor="profile-name" className="block text-sm font-medium text-secondary-700 mb-1.5">
              {t('profile.fullName')}
            </label>
            <input
              id="profile-name"
              name="full_name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full text-sm rounded-md border border-secondary-300 px-4 py-3 text-secondary-900 placeholder:text-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
            />
          </div>

          <div>
            <label htmlFor="profile-email" className="block text-sm font-medium text-secondary-700 mb-1.5">
              {t('profile.emailAddress')}
            </label>
            <input
              id="profile-email"
              type="email"
              value={email}
              disabled
              className="w-full text-sm rounded-md border border-secondary-200 bg-secondary-50 px-4 py-3 text-secondary-400 cursor-not-allowed"
            />
            <p className="mt-1.5 text-xs text-secondary-400">
              {t('profile.emailNote')}
            </p>
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
              {error}
            </p>
          )}
          {message && (
            <p className="text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3 py-2.5">
              {message}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-md px-6 py-3 transition-colors whitespace-nowrap"
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin text-base" />
                {t('profile.saving')}
              </>
            ) : (
              t('profile.saveChanges')
            )}
          </button>
        </form>
      </div>
    </div>
  );
}